'use client';

import { c as _c } from "react/compiler-runtime";
import { useFeatureFlag } from '@posthog/next';
import dynamic from 'next/dynamic';
import { use, useCallback, useState } from 'react';
import { useIsClient, useMediaQuery, useSessionStorage } from 'usehooks-ts';
import { useUAContext } from '@/context/UAContext';
import { useExitIntent } from '@/hooks/useExitIntent';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { getWorldsDomain } from '@/lib/configs';
import { type RoomContentType } from '@/services/cms';
import type { WorldRecommendationsResult } from '@/types/world-recommendations';
const ExitNudgeDrawer = dynamic(() => import('@/app/[locale]/(worlds)/[roomId]/components/ExitNudgeDrawer'), {
  ssr: false
});
const ExitRecommendationModal = dynamic(() => import('@/app/[locale]/(worlds)/[roomId]/components/ExitRecommendationModal'), {
  ssr: false
});
const EXIT_RECOMM_FLAG = 'content-detail-exit-recommendation';
type PreviewImageUrls = [string | undefined, string | undefined];
type ExitRetentionFlowProps = {
  contentType: RoomContentType | null | undefined;
  recommendationsPromise: Promise<WorldRecommendationsResult>;
};
export default function ExitRetentionFlow(t0) {
  const $ = _c(34);
  const {
    contentType,
    recommendationsPromise
  } = t0;
  const {
    isMobile
  } = useUAContext();
  const flag = useFeatureFlag(EXIT_RECOMM_FLAG);
  const isClient = useIsClient();
  const isEmbedded = useIsEmbedded();
  const recommendationResult = use(recommendationsPromise);
  let t1;
  let worlds;
  if ($[0] !== recommendationResult.ok || $[1] !== recommendationResult.rooms) {
    worlds = recommendationResult.ok ? recommendationResult.rooms : [];
    t1 = worlds.map(_temp).filter(_temp2);
    $[0] = recommendationResult.ok;
    $[1] = recommendationResult.rooms;
    $[2] = t1;
    $[3] = worlds;
  } else {
    t1 = $[2];
    worlds = $[3];
  }
  const previewImages = t1;
  let t2;
  if ($[4] !== previewImages[0] || $[5] !== previewImages[1]) {
    t2 = [previewImages[0], previewImages[1]];
    $[4] = previewImages[0];
    $[5] = previewImages[1];
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const previewImageUrls = t2;
  const supportsExitIntent = useMediaQuery("(hover: hover) and (pointer: fine)");
  let t3;
  if ($[7] !== isClient) {
    t3 = isClient && (process.env.NODE_ENV === "development" || window.location.hostname === getWorldsDomain());
    $[7] = isClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isViverseHost = t3;
  const isActive = flag?.variant === "test" && supportsExitIntent && !isMobile && isViverseHost && !isEmbedded;
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      initializeWithValue: false
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [isNudgeDismissed, setIsNudgeDismissed] = useSessionStorage("exit-nudge-dismissed", false, t4);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setNudgeOpen(true);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleExitIntent = t5;
  const t6 = isActive && !isNudgeDismissed && !nudgeOpen && !modalOpen;
  let t7;
  if ($[11] !== t6) {
    t7 = {
      enabled: t6,
      onTrigger: handleExitIntent
    };
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    reset
  } = useExitIntent(t7);
  let t8;
  if ($[13] !== reset) {
    t8 = () => {
      setNudgeOpen(false);
      reset();
    };
    $[13] = reset;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleCloseNudge = t8;
  let t9;
  if ($[15] !== setIsNudgeDismissed) {
    t9 = () => {
      setIsNudgeDismissed(true);
      setNudgeOpen(false);
    };
    $[15] = setIsNudgeDismissed;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleDismissNudge = t9;
  let t10;
  if ($[17] !== reset) {
    t10 = () => {
      setModalOpen(false);
      reset();
    };
    $[17] = reset;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleCloseModal = t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setNudgeOpen(false);
      setModalOpen(true);
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleViewAll = t11;
  if (!isActive) {
    return null;
  }
  let t12;
  if ($[20] !== contentType || $[21] !== handleCloseNudge || $[22] !== handleDismissNudge || $[23] !== nudgeOpen || $[24] !== previewImageUrls) {
    t12 = <ExitNudgeDrawer open={nudgeOpen} onClose={handleCloseNudge} onDismiss={handleDismissNudge} onViewAll={handleViewAll} contentType={contentType} previewImageUrls={previewImageUrls} />;
    $[20] = contentType;
    $[21] = handleCloseNudge;
    $[22] = handleDismissNudge;
    $[23] = nudgeOpen;
    $[24] = previewImageUrls;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== contentType || $[27] !== handleCloseModal || $[28] !== modalOpen || $[29] !== worlds) {
    t13 = <ExitRecommendationModal open={modalOpen} onClose={handleCloseModal} contentType={contentType} worlds={worlds} />;
    $[26] = contentType;
    $[27] = handleCloseModal;
    $[28] = modalOpen;
    $[29] = worlds;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function _temp2(image) {
  return Boolean(image);
}
function _temp(room) {
  return room.image || undefined;
}